<template>
  <div class="work-item-task-timeline">
    <a-spin :spinning="loading">
      <a-alert
        v-if="loadError"
        type="error"
        show-icon
        class="timeline-alert"
        :message="tv('timelineLoadFailed')"
      >
        <template #action>
          <a-button size="small" @click="loadRecords">{{ t('common.retry') }}</a-button>
        </template>
      </a-alert>

      <template v-else>
        <div v-if="currentTask" class="current-task">
          <div class="current-task-title">{{ tv('currentTask') }}</div>
          <a-descriptions size="small" :column="3" bordered>
            <a-descriptions-item :label="tv('taskId')">
              {{ currentTask.id }}
            </a-descriptions-item>
            <a-descriptions-item :label="tv('taskStatus')">
              <a-tag :color="workItemStatusColor(currentTask.status)">
                {{ statusLabel(currentTask.status) }}
              </a-tag>
            </a-descriptions-item>
            <a-descriptions-item :label="tv('taskRound')">
              {{ currentTask.round }}
            </a-descriptions-item>
          </a-descriptions>
        </div>
        <a-empty v-else :description="tv('noCurrentTask')" />

        <div class="review-block">
          <div class="review-title">{{ tv('timelineTitle') }}</div>
          <a-empty
            v-if="!loading && records.length === 0"
            :description="tv('timelineEmpty')"
          />
          <a-timeline v-else-if="records.length > 0">
            <a-timeline-item v-for="record in records" :key="record.id">
              <div class="review-item">
                <div class="review-meta">
                  {{ tv('reviewRound', { n: record.round }) }}
                  <span v-if="record.created_at" class="review-time">
                    {{ formatTime(record.created_at) }}
                  </span>
                </div>
                <div class="review-comment">
                  {{ record.comment || t('common.none') }}
                </div>
              </div>
            </a-timeline-item>
          </a-timeline>
        </div>
      </template>
    </a-spin>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import type { WorkItem } from '@/modules/project/api/work-item'
import { useProjectI18n } from '@/modules/project/i18n/use-project-i18n'
import { workItemStatusColor } from '@/modules/project/utils/work-item-navigation'
import { getTaskReviewRecords, type TaskReviewRecord } from '@/modules/task/api/task'
import type { BatchRequestConfig } from '@/shared/api/use-api-batch'

const props = defineProps<{
  workItem: WorkItem
}>()

const { t } = useI18n()
const { tv } = useProjectI18n('detail')

const loading = ref(false)
const loadError = ref(false)
const records = ref<TaskReviewRecord[]>([])

const currentTask = computed(() => props.workItem.current_task ?? null)

function statusLabel(status: string): string {
  const keyMap: Record<string, string> = {
    pending: 'statusPending',
    processing: 'statusProcessing',
    submitted: 'statusSubmitted',
    completed: 'statusCompleted',
    rejected_at_assignment: 'statusRejected',
    no_task: 'statusNoTask',
  }
  return tv(keyMap[status] || 'statusUnknown')
}

function formatTime(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString()
}

async function loadRecords() {
  const taskId = currentTask.value?.id ?? props.workItem.current_task_id
  if (!taskId) {
    records.value = []
    loadError.value = false
    return
  }

  loading.value = true
  loadError.value = false
  try {
    const result = await getTaskReviewRecords(taskId, {
      skipErrorToast: true,
      errorBatchId: `task-timeline-${taskId}`,
    } satisfies BatchRequestConfig)
    records.value = Array.isArray(result) ? result : []
  } catch (error) {
    console.error('Failed to load task review records:', error)
    loadError.value = true
    records.value = []
  } finally {
    loading.value = false
  }
}

watch(
  () => props.workItem.current_task_id,
  () => {
    void loadRecords()
  },
  { immediate: true },
)
</script>

<style lang="less" scoped>
.work-item-task-timeline {
  padding: 8px 8px 4px;
}

.timeline-alert {
  margin-bottom: 12px;
}

.current-task-title,
.review-title {
  margin-bottom: 8px;
  font-weight: 600;
}

.review-block {
  margin-top: 16px;
}

.review-item {
  .review-meta {
    color: #595959;
    font-size: 13px;
  }

  .review-time {
    margin-left: 8px;
    color: #8c8c8c;
  }

  .review-comment {
    margin-top: 4px;
    color: #262626;
  }
}
</style>
